import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Table, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { serverFetch } from "@server";
import { getLogUserResult } from "@extensions/helper";
import { tableRenderDateTime } from "@extensions/table-render";
import { isMobile } from "@extensions/design";
import LogMonitorItem from "./log-monitor-item";

const LogMonitor = ({ vid, id }) => {
  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [objectVids, setObjectVids] = useState([]);
  const [vids, setVids] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const loadData = useCallback(() => {
    if (!vid || !id) {
      message.warning("Не указаны параметры объекта");
      return;
    }

    setLoading(true);
    
    const params = new URLSearchParams();
    params.append("ObjectVid", vid);
    params.append("ObjectId", id);

    serverFetch(`LogUser/ListByObject?${params.toString()}`, { method: 'GET' })
      .then((data) => {
        setItems(data || []);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.UserMessage || "Ошибка загрузки данных");
        console.log(e);
      });
  }, [vid, id]);

  useEffect(() => {
    if (visible) {
      // Загружаем справочники для отображения
      serverFetch("loguser/vids")
        .then(data => setVids(data))
        .catch(e => console.log(e.message));
      serverFetch("loguser/objectvids")
        .then(data => setObjectVids(data))
        .catch(e => console.log(e.message));
      
      // Загружаем данные
      loadData();
    }
  }, [visible, loadData]);

  // Функция для проверки, есть ли хотя бы одно заполненное значение в колонке
  const hasDataInColumn = useCallback((column, data) => {
    // Всегда показываем колонки с флагом alwaysShow
    if (column.alwaysShow) {
      return true;
    }

    const dataIndex = column.dataIndex;
    if (!dataIndex) {
      return true; // Если нет dataIndex, показываем колонку
    }

    // Проверяем, есть ли хотя бы одна строка с заполненным значением
    return data.some(item => {
      const value = item[dataIndex];
      // Считаем значение заполненным, если оно не null, не undefined и не пустая строка
      return value !== null && value !== undefined && value !== '';
    });
  }, []);

  // Создаем и фильтруем колонки на основе данных
  const columns = useMemo(() => {
    const getLogUserVidName = (value) => {
      const vidObj = vids && vids.find(i => i.id === value);
      return vidObj ? vidObj.name : `[${value}]`;
    };

    const getLogUserVidObjectName = (value) => {
      const vidObj = objectVids && objectVids.find(i => i.id === value);
      return vidObj ? vidObj.name : `[${value}]`;
    };

    const baseColumns = [
      { title: '#', dataIndex: 'id', width: "1em", align: 'center', alwaysShow: true },
      { title: 'Дата', dataIndex: 'createdOn', width: "1em", align: 'center', render: tableRenderDateTime, alwaysShow: true },
      { title: 'Вид', dataIndex: 'vid', width: "1em", align: 'left', render: (_, record) => getLogUserVidName(record.vid), alwaysShow: true },
      { title: 'Клиент', dataIndex: 'clientName', width: "1em", align: 'left' },
      { title: 'Телефон', dataIndex: 'clientPhone', width: "1em", align: 'left' },
      { title: 'Компания', dataIndex: 'companyName', width: "1em", align: 'left' },
      { title: 'Пользователь', dataIndex: 'userName', width: "1em", align: 'left' },
      { title: 'Email', dataIndex: 'userEmail', width: "1em", align: 'left' },
      { title: 'Комментарий', dataIndex: 'comment', width: "1em", align: 'left' },
      { title: 'Результат', dataIndex: 'result', width: "1em", align: 'center', render: (value) => getLogUserResult(value), alwaysShow: true },
      { title: 'Объект', dataIndex: 'objectVid', width: "1em", align: 'left', render: (_, record) => getLogUserVidObjectName(record.objectVid), alwaysShow: true },
    ];

    return baseColumns.filter(column => hasDataInColumn(column, items));
  }, [items, vids, objectVids, hasDataInColumn]);

  const onCancel = () => {
    setVisible(false);
    setSelectedItem(null);
  };

  const onCloseItem = () => setSelectedItem(null);

  return (
    <>
      <Button onClick={() => setVisible(true)} disabled={!id}>Монитор</Button>
      {visible && (
        <div className="sport-modal">
          <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onCancel()}>
            <div className={isMobile ? "sport-modal-content mobile-content" : "sport-modal-content"} 
                 style={isMobile ? {} : { width: 1300 }} 
                 onMouseDown={e => e.stopPropagation()}>
              <div className="sport-modal-title">
                <span>Монитор действий</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Button onClick={loadData} loading={loading} size="small">Обновить</Button>
                  <CloseOutlined className="sport-section-title-close" onClick={onCancel} style={{ zIndex: 9 }} />
                </div>
              </div>
              <div style={{ padding: '16px' }}>
                <Table
                  dataSource={items}
                  columns={columns}
                  pagination={false}
                  rowKey="id"
                  loading={loading}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  onRow={(record) => ({
                    onClick: () => setSelectedItem(record),
                    style: { cursor: 'pointer' }
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedItem && <LogMonitorItem id={selectedItem.id} onClose={onCloseItem} />}
    </>
  );
};

export default LogMonitor;
